import z from "zod";
import ZodSchemaUtil from "util/schema-helpers/ZodSchemaUtil";
import { UserSubscriptionStatusSchema } from "rdx/modules/team/schemas";
import {
  EnrollmentSchema,
  EntServiceSlugsSchema,
  EntStaffSlugsSchema,
  SeatServiceSchema,
} from "../../enrollment/schemas";
import { ProjectEligibilities, UserAutocompletePathEnum, EnterpriseCompModelEnum } from "../types/enums";
import { ExperienceLevelEnum } from "../../profile/enums";
import { AvatarSchema } from "./avatar";

export const AddressSchema = z.object({
  street: z.string().nullable(),
  unit: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  country: z.string().nullable(),
  status: ZodSchemaUtil.makeStringLiteralUnion(["unverified", "verified", "lookup_error", "locked"]),
  lat: z.union([z.number(), z.string()]).nullable(),
  long: z.union([z.number(), z.string()]).nullable(),
  googleMapsUrl: z.string().nullable(),
  geocodePrecision: z.string().nullable(),
  geocodeLat: z.union([z.number(), z.string()]).nullable(),
  geocodeLong: z.union([z.number(), z.string()]).nullable(),
});

export const MarginBonusSchema = z.object({ endDate: z.string(), percentage: z.string(), id: z.number() });

export const CreateMarginBonusSchema = z.object({
  userId: z.number(),
  params: MarginBonusSchema.omit({ id: true }),
});

export const EndMarginBonusSchema = z.object({
  userId: z.number(),
  id: z.number(),
});

export const UserLiteSchema = z.object({
  id: z.number(),
  fullName: z.string(),
  email: z.string(),
  phone: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  vanityName: z.string(),
  locale: z.string(),
  spokenLanguages: z.array(z.string()),
  terminatedAt: z.string().nullable(),
  bio: z.string().nullable(),
  confReg: z.string().nullable(),
  proposalBio: z.string().nullable(),
  attendedPowurOn: z.boolean(),
  avatar: AvatarSchema,
  sellerAvatar: AvatarSchema,
  seatId: z.number().nullish(),
  enterpriseId: z.number().nullish(),
  enterpriseSellerPercentage: z.string().nullish(),
  enterpriseSellerInvitesAllowed: z.boolean().nullish(),
  enterpriseName: z.string().nullish(),
  contractCount: z.number().nullish(),
  proposalCount: z.number().nullish(),
  conversionRate: z.string().nullish(),
  cancellationRate: z.string().nullish(),
  isOrg: z.boolean().nullish(),
  marginBonusPercentage: MarginBonusSchema.nullish(),
  paymentsPausedAt: z.string().nullable(),
});

export const UserEnterpriseSchemaProps = {
  id: z.number().optional(),
  external: z.boolean(),
  isOwner: z.boolean(),
  compTypeLocked: z.boolean().nullish(),
  dealerCreated: z.boolean().optional(),
  parentEnterpriseId: z.number().nullish(),
  recognitionEmails: z.boolean(),
  compensationEmails: z.boolean(),
  eventEmails: z.boolean(),
  promotionEmails: z.boolean(),
  newsletterEmails: z.boolean(),
  platformEmails: z.boolean(),
  trainingEmails: z.boolean(),
  accountAndTeamEmails: z.boolean(),
  owner: z.object({ id: z.number() }),
};

export const UserItemSchema = z
  .object({
    metrics: z.array(z.record(z.number())),
    mentorRatings: z
      .object({
        contractCount: z.number(),
        installCount: z.number(),
        proposalCount: z.number(),
        totalCount: z.number(),
        contractAvg: z.string(),
        installAvg: z.string(),
        proposalAvg: z.string(),
        meanOfAvgs: z.string(),
      })
      .nullish(),
    rank: z.number(),
    maritalStatus: ZodSchemaUtil.makeStringLiteralUnion(["married", "single"]).nullable(),
    dob: z.string().nullable(),
    defaultAdvance: z.boolean(),
    defaultAdvanceSet: z.boolean(),
    gender: ZodSchemaUtil.makeStringLiteralUnion(["male", "female", "non_binary", "prefer_not_to_say"]).nullable(),
    areasServiced: z.array(z.string()),
    sponsors: z.array(z.number()),
    timezone: z.string().nullable(),
    qualifiedUntil: z.string().nullable(),
    lastQualifyingActivityAt: z.string().nullable(),
    subscriptionStatus: UserSubscriptionStatusSchema,
    authFormat: ZodSchemaUtil.makeStringLiteralUnion(["any_auth", "password", "facebook", "google"]),
    orgName: z.string().nullable(),
    marginVisibility: z.boolean(),
    commissionVisibility: z.boolean(),
    sellerCalendlyUrl: z.string().nullable(),
    homeownerCalendlyUrl: z.string().nullable(),
    enterprise: z
      .discriminatedUnion("compType", [
        z.object({
          ...UserEnterpriseSchemaProps,
          compType: z.literal(EnterpriseCompModelEnum.COGS),
        }),
        z.object({
          ...UserEnterpriseSchemaProps,
          compType: z.literal(EnterpriseCompModelEnum.HOLDBACK),
          orgHoldbackPpw: z.number().optional(),
          powurHoldbackPpw: z.number().optional(),
          canRecruitEntOwners: z.boolean(),
        }),
        z.object({
          ...UserEnterpriseSchemaProps,
          compType: z.literal(EnterpriseCompModelEnum.POWUR_LINE),
          entBaselinePpw: z.number().optional(),
          leadershipCommissionPpw: z.number().optional(),
          sellerPowurlinePpw: z.number().optional(),
          powurlineManagerOverride: z.boolean().optional(),
          canRecruitEntOwners: z.boolean(),
        }),
      ])
      .nullish(),
    displayRoles: z.array(z.array(z.string())),
    roles: z.array(z.string()),
    sponsor: UserLiteSchema.nullish(),
    ewalletUsername: z.string().nullish(),
    ipayoutAdress: z.string().nullish(),
    manager: UserLiteSchema.nullish(),
    relatedLevel: z.number(),
    certified: z.boolean(),
    integrityModules: z.array(z.string()).nullable(),
    experience: z.nativeEnum(ExperienceLevelEnum).nullable(),
    projectRequirementMet: z.boolean(),
    projectRequirement: z.number(),
    projectRequirementProgress: z.number(),
    tierThreeProjectRequirementMet: z.boolean(),
    tier2OrEnterpriseCertified: z.boolean(),
    certifications: z.array(z.string()),
    enrollment: z.lazy(() => EnrollmentSchema.nullish()),
    emailChangeRequest: z
      .object({
        newEmail: z.string(),
        expiresAt: z.string(),
      })
      .nullish(),
    payEligibilities: z.array(z.nativeEnum(ProjectEligibilities)).refine((els) => new Set(els).size === els.length, {
      message: "Duplicate values are not allowed",
    }),
    address: AddressSchema.nullish(),
    seat: z
      .object({
        id: z.number(),
        startDay: z.string(),
        paymentStatus: z.literal("seated"),
      })
      .nullish(),
  })
  .merge(UserLiteSchema);

export const UserSchema = z
  .object({
    stripeOauthId: z.number().nullable(),
    metaData: z.object({}),
    ambassadorEarnings: z.number().nullish(),
    dashboardPhase: z.number(),
    kind: ZodSchemaUtil.makeStringLiteralUnion([
      "consultant",
      "ambassador",
      "installer",
      "enterprise",
      "enterprise_pro",
    ]).nullable(),
    manualKindSelected: z.boolean(),
    inboxCount: z.number(),
    myProjectsCount: z.number(),
    tooltipViewedOn: z.record(z.string()).nullable(),
    createdAt: z.string(),
    emailHash: z.string(),
    enterpriseType: z.string().nullable(),
    seatAssignments: z.array(
      z.object({
        serviceId: z.number(),
        slug: z.union([z.lazy(() => EntServiceSlugsSchema), z.lazy(() => EntStaffSlugsSchema)]),
      }),
    ),
    acceptedLatestTos: z.boolean(),
  })
  .merge(UserItemSchema);

export const CoordinatorSchema = UserLiteSchema.pick({
  id: true,
  fullName: true,
  email: true,
  phone: true,
  avatar: true,
}).merge(z.object({ sellerCalendlyUrl: z.string() }));

export const AmbassadorSchema = UserLiteSchema.pick({
  id: true,
  fullName: true,
  email: true,
  phone: true,
  avatar: true,
});

export const AgentSchema = CoordinatorSchema.omit({ sellerCalendlyUrl: true }).merge(
  z.object({ siteSurveyor: z.boolean() }),
);

export const MentorSchema = AgentSchema.merge(
  z.object({
    metrics: z.array(z.record(z.number())).nullish(),
    mentorRatings: z.record(z.union([z.string(), z.number()])).nullish(),
    locale: z.string().nullish(),
    spokenLanguages: z.array(z.string()).nullish(),
    sellerCalendlyUrl: z.string().nullish(),
    homeownerCalendlyUrl: z.string().nullish(),
    address: z
      .object({
        city: z.string(),
        state: z.string(),
      })
      .nullish(),
  }),
).omit({ siteSurveyor: true });

const PendingSeatSchema = z.object({
  id: z.number(),
  registrationId: z.number(),
  serviceIds: z.array(z.number()),
  discardedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  enterpriseId: z.number(),
  userId: z.number().nullable(),
  enterpriseSettingId: z.number().nullable(),
});

const SeatSettingsSchema = z.object({
  accountAndTeamEmails: z.boolean(),
  autoOfferAcceptance: z.boolean(),
  cobrandingActivated: z.boolean(),
  compensationEmails: z.boolean(),
  eventEmails: z.boolean(),
  installerAssignment: z.string().nullable(),
  installerAssignmentEmail: z.string().nullable(),
  managerCommission: z.number().nullable(),
  managerMarginAdjustment: z.boolean(),
  managerMinimumMargin: z.string(),
  marginFixed: z.boolean(),
  mentorshipRequirement: z.number(),
  minimumMargin: z.number().nullable(),
  newsletterEmails: z.boolean(),
  offerPercentage: z.string().nullable(),
  offerSplitPercentage: z.string().nullable(),
  payForProposals: z.boolean(),
  platformEmails: z.boolean(),
  promotionEmails: z.boolean(),
  proposalMargin: z.string().nullable(),
  recognitionEmails: z.boolean(),
  selfGenSplitPercentage: z.string().nullable(),
  sellerCommissionVisibility: z.boolean(),
  sellerInvitesAllowed: z.boolean(),
  sellerMarginVisibility: z.boolean(),
  sellerPercentage: z.string().nullable(),
  supportCommission: z.number().nullable(),
  title: z.string().nullable(),
  trainingEmails: z.boolean(),
});

export const EnterpriseUserSchema = z
  .object({
    seated: z.boolean(),
    salesPro: z.boolean(),
    orgMentor: z.boolean(),
    entCloserOutside: z.boolean(),
    staffServices: z.array(
      z.lazy(() => SeatServiceSchema.merge(z.object({ slug: z.lazy(() => EntStaffSlugsSchema) }))),
    ),
    currentStaffServicesRoleId: z.number().nullable(),
    pendingSeat: PendingSeatSchema.nullish(),
    seatAssignments: z.array(
      z.object({
        role: z.lazy(() => EntStaffSlugsSchema),
        status: z.string(),
        start: z.string(),
        end: z.string(),
        org: z.string().nullish(),
      }),
    ),
    ewallet: z.object({
      ewalletUsername: z.string(),
      ipayoutAddress: z.string().nullish(),
    }),
    settings: SeatSettingsSchema.nullish(),
    enterpriseSettings: SeatSettingsSchema.nullish(),
  })
  .merge(UserItemSchema);

export const GetUserAutocompleteParamsSchema = z.object({
  userPath: z.nativeEnum(UserAutocompletePathEnum),
  search: z.string(),
});

export const AutocompleteUserSchema = z.object({
  id: z.number(),
  fullName: z.string(),
  avatar: AvatarSchema,
  avatarUrls: AvatarSchema,
});

export const GetUserAutocompleteResponseSchema = z.object({
  status: z.number(),
  body: z.object({
    users: z.array(AutocompleteUserSchema),
  }),
});

export const UpdateUserParamsSchema = z.object({
  userId: z.number(),
  legalBusinessName: z.string().optional(),
});

export const UpdateUserResponseSchema = z.object({
  status: z.number(),
  body: UserItemSchema,
});

export const PauseUserPaymentsParamsSchema = z.object({
  userId: z.number(),
});
